import React from 'react';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { PreVerifiedToolInputRecord } from 'types/dynamic-forms/records/PreVerifiedToolInputRecord';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import useProperty from '../../hooks/useProperty';
import usePropertyPath, { PropertyPathStackProvider } from '../../hooks/usePropertyPath';
import useDataSource from '../../hooks/useDataSource';
import { DocumentDataSourceOption } from 'types/dynamic-forms/DocumentDataSourceOption';

const PreVerifiedToolInputRenderer: React.FC<PreVerifiedToolInputRecord> = ({ propertyId }) => {
  // The propertyId is the parent object property, children are: preVerifiedTool, toolVersion, toolVerifier
  const basePath = usePropertyPath(propertyId);
  const { isVisible, isDisabled } = useProperty<any>(basePath);

  if (!isVisible) {
    return null;
  }

  return (
    <PropertyPathStackProvider pushPath={[propertyId]}>
      <PreVerifiedToolInputContent isDisabled={isDisabled} />
    </PropertyPathStackProvider>
  );
};

const PreVerifiedToolInputContent: React.FC<{ isDisabled: boolean }> = ({ isDisabled }) => {
  // Inside PropertyPathStackProvider, we can use usePropertyPath with just the child propertyId
  const preVerifiedToolPath = usePropertyPath('id');
  const preVerifiedToolVersionPath = usePropertyPath('versionId');
  const preVerifiedToolVerifierPath = usePropertyPath('verifierName');

  const { value: preVerifiedToolValue, setValue: setPreVerifiedToolValue, errorMessage: preVerifiedToolError, isDisabled: isPreVerifiedToolDisabled } = useProperty<string | null>(preVerifiedToolPath);
  const { value: preVerifiedToolVersionValue, setValue: setPreVerifiedToolVersionValue, errorMessage: preVerifiedToolVersionError, isDisabled: isPreVerifiedToolVersionDisabled } = useProperty<string | null>(preVerifiedToolVersionPath);
  const { value: preVerifiedToolVerifierValue, setValue: setPreVerifiedToolVerifierValue, errorMessage: preVerifiedToolVerifierError, isDisabled: isPreVerifiedToolVerifierDisabled } = useProperty<string>(preVerifiedToolVerifierPath);

  const { options: preVerifiedToolOptions } = useDataSource(preVerifiedToolPath);
  const { options: preVerifiedToolVersionOptions } = useDataSource(preVerifiedToolVersionPath);

  const preVerifiedToolSelectOptions = preVerifiedToolOptions.map((opt: DocumentDataSourceOption) => ({
    label: opt.label,
    value: opt.value,
    isDisabled: opt.isDisabled,
  }));
  const preVerifiedToolVersionSelectOptions = preVerifiedToolVersionOptions.map((opt: DocumentDataSourceOption) => ({
    label: opt.label,
    value: opt.value,
    isDisabled: opt.isDisabled,
  }));

  // Find selected tool version to get verifier name
  const selectedToolVersion = preVerifiedToolVersionOptions.find((o: DocumentDataSourceOption) => o.value === preVerifiedToolVersionValue);
  const verifierName = selectedToolVersion?.meta?.verifierName || '';

  // Update tool verifier when tool version changes
  React.useEffect(() => {
    if (preVerifiedToolVersionValue && verifierName) {
      setPreVerifiedToolVerifierValue(verifierName);
    } else {
      setPreVerifiedToolVerifierValue('');
    }
  }, [preVerifiedToolVersionValue, verifierName, setPreVerifiedToolVerifierValue]);

  // Clear tool version when tool changes
  React.useEffect(() => {
    if (preVerifiedToolValue) {
      // Tool version will be filtered automatically by parentDataSourceValue
      // But we should clear the selected value if it's no longer valid
      const currentVersion = preVerifiedToolVersionOptions.find((o: DocumentDataSourceOption) => o.value === preVerifiedToolVersionValue);
      if (currentVersion && currentVersion.parentDataSourceValue !== preVerifiedToolValue) {
        setPreVerifiedToolVersionValue(null);
      }
    } else {
      setPreVerifiedToolVersionValue(null);
      setPreVerifiedToolVerifierValue('');
    }
  }, [preVerifiedToolValue, preVerifiedToolVersionOptions, preVerifiedToolVersionValue, setPreVerifiedToolVersionValue, setPreVerifiedToolVerifierValue]);

  return (
    <>
      <WizardSelectControlledComponent
        name="preVerifiedToolId"
        options={preVerifiedToolSelectOptions}
        value={preVerifiedToolSelectOptions.find((o) => o.value === preVerifiedToolValue) ?? null}
        label="Pre-verified Tool"
        tooltip="Select the pre-verified tool used for this EPD"
        onChanged={(_: string, value: any) => setPreVerifiedToolValue(value ?? null)}
        error={preVerifiedToolError ?? undefined}
        disabled={isDisabled || isPreVerifiedToolDisabled}
        {...reactSelectPropsForHtmlLabel}
      />

      <WizardSelectControlledComponent
        name="toolVersionId"
        options={preVerifiedToolVersionSelectOptions}
        value={preVerifiedToolVersionSelectOptions.find((o) => o.value === preVerifiedToolVersionValue) ?? null}
        label="Tool Version"
        tooltip="Select the version of the pre-verified tool"
        onChanged={(_: string, value: any) => setPreVerifiedToolVersionValue(value ?? null)}
        error={preVerifiedToolVersionError ?? undefined}
        disabled={isDisabled || isPreVerifiedToolVersionDisabled || !preVerifiedToolValue}
        {...reactSelectPropsForHtmlLabel}
      />

      <WizardTextInputControlledComponent
        name="toolVerifier"
        label="Tool Verifier"
        tooltip="The verifier of the selected tool version (read-only)"
        value={verifierName}
        onChange={() => {}}
        onChanged={() => {}}
        disabled={true}
        error={undefined}
      />
    </>
  );
};

export default PreVerifiedToolInputRenderer;

